import { range } from "@/utils";

/**
 * Generate a sequence of numbers for use in a pagination system, the clever way.
 * @author Bramus Van Damme <bramus@bram.us>
 *
 * The algorithm always returns the same amount of items in the sequence,
 * independent of the position of the current page.
 *
 * Example rows generated:
 * (adjusted to indicate the current page between [] + leading 0s added)
 *
 * [01]-02-03-04-05-06-07-08-..-73-74
 * 01-[02]-03-04-05-06-07-08-..-73-74
 * 01-02-[03]-04-05-06-07-08-..-73-74
 * 01-02-03-[04]-05-06-07-08-..-73-74
 * 01-02-03-04-[05]-06-07-08-..-73-74
 * 01-02-03-04-05-[06]-07-08-..-73-74
 * 01-02-..-05-06-[07]-08-09-..-73-74
 * 01-02-..-06-07-[08]-09-10-..-73-74
 * 01-02-..-07-08-[09]-10-11-..-73-74
 * ...
 * 01-02-..-65-66-[67]-68-69-..-73-74
 * 01-02-..-66-67-[68]-69-70-..-73-74
 * 01-02-..-67-68-[69]-70-71-72-73-74
 * 01-02-..-67-68-69-[70]-71-72-73-74
 * 01-02-..-67-68-69-70-[71]-72-73-74
 * 01-02-..-67-68-69-70-71-[72]-73-74
 * 01-02-..-67-68-69-70-71-72-[73]-74
 * 01-02-..-67-68-69-70-71-72-73-[74]
 *
 */
export const paginationSequence = ({
	page,
	pageCount,
	edges = 2,
	onEachSide = 2,
	glue = "…",
}: {
	page: number;
	pageCount: number;
	edges?: number;
	onEachSide?: number;
	glue?: string;
}) => {
	// Define the number of items we would generate in a normal scenario
	// (viz. lots of pages, current page in the middle):
	//
	// numItemsInSequence = the current page + the number of items surrounding
	// the current page (left and right) + the number of items at the edges
	// of the generated sequence (left and right) + the glue in between the
	// different parts generated
	//
	// The goal is to enforce all sequences generated to have this amount
	// of items. By default this magic number would be 11, as seen/counted
	// in this sequence: 1-02-..-11-12-[13]-14-15-..-88-74
	const numItemsInSequence = 1 + onEachSide * 2 + edges * 2 + 2;

	// page cannot be greater than pageCount.
	const reworkedCurPage = Math.min(page, pageCount);

	// The value we're about to return$
	let finalSequence = [];

	// If we have less than numItemsInSequence pages in total, there is no need to
	// start calculating but just return the full sequence, starting at 1
	if (pageCount <= numItemsInSequence) {
		finalSequence = range({ start: 1, end: pageCount });
	}

	// We have more pages than numItemsInSequence, start calculating
	else {
		// If we have no forced amount of items on the edges, then the
		// sequence must start from the current page number instead of 1
		const start = edges > 0 ? 1 : reworkedCurPage;

		// Parts of the sequence we'll be generating
		const sequence = {
			leftEdge: null,
			glueLeftCenter: null,
			centerPiece: null,
			glueCenterRight: null,
			rightEdge: null,
		} as {
			leftEdge: null | Array<number | string>;
			glueLeftCenter: null | Array<number | string>;
			centerPiece: null | Array<number | string>;
			glueCenterRight: null | Array<number | string>;
			rightEdge: null | Array<number | string>;
		};

		// If the current page is nearby the left edge (viz. page is
		// less than half of numItemsInSequence away from left edge):
		// Don't generate a Center Piece, but extend the left part as
		// the left part would otherwise overlap the center piece.
		if (reworkedCurPage < numItemsInSequence / 2) {
			sequence.leftEdge = range({
				start: 1,
				end: Math.ceil(numItemsInSequence / 2) + onEachSide,
			});
			sequence.centerPiece = [glue];
			if (edges > 0)
				sequence.rightEdge = range({
					start: pageCount - (edges - 1),
					end: pageCount,
				});
		}

		// If the current page is nearby the right edge (viz. page is
		// less than half of numItemsInSequence away from right edge):
		// Don't generate a center piece but extend the right part as
		// the right part would otherwise overlap the center piece.
		else if (reworkedCurPage > pageCount - numItemsInSequence / 2) {
			if (edges > 0) sequence.leftEdge = range({ start, end: edges });
			sequence.centerPiece = [glue];
			sequence.rightEdge = range({
				start: Math.min(
					pageCount - Math.floor(numItemsInSequence / 2) - onEachSide,
					reworkedCurPage - onEachSide,
				),
				end: pageCount,
			});
		}

		// The current page falls somewhere in the middle:
		// Generate ranges normally
		else {
			// Center Piece
			sequence.centerPiece = range({
				start: reworkedCurPage - onEachSide,
				end: reworkedCurPage + onEachSide,
			});

			// Left/Right Edges (only if we requested)
			if (edges > 0) sequence.leftEdge = range({ start: start, end: edges });
			if (edges > 0)
				sequence.rightEdge = range({
					start: pageCount - (edges - 1),
					end: pageCount,
				});

			// The glue we'll use to stick left, center, and right together
			// Special case: If the gap between left and center is only one
			// unit, don't add '...' but add that number instead
			sequence.glueLeftCenter = sequence.centerPiece[0] === edges + 2 ? [edges + 1] : [glue];
			sequence.glueCenterRight = [glue];
		}

		// Join all (non-empty) parts of sequence into the final sequence
		finalSequence = Object.values(sequence)
			.filter((v) => v !== null)
			.flat();
	}

	return finalSequence;
};
